import React, { useState } from 'react';
import { AlertTriangle, ChevronDown, ClipboardList, Sparkles, Target } from 'lucide-react';
import type { RoutyAppointmentBriefArtifact } from '../../business/routy/routyArtifacts';

/** Simple labeled bullet-list block used by the sub-sections below. */
const BulletSection: React.FC<{
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  items: string[];
  toneClasses: { wrap: string; label: string; dot: string; text: string };
}> = ({ icon: Icon, label, items, toneClasses }) => (
  <div className={`rounded-lg px-3 py-2.5 ${toneClasses.wrap}`}>
    <p className={`inline-flex items-center gap-1.5 text-2xs font-semibold uppercase tracking-wider ${toneClasses.label}`}>
      <Icon className="h-3.5 w-3.5" />
      {label}
    </p>
    <ul className="mt-1.5 space-y-1.5">
      {items.map((item, i) => (
        <li key={i} className={`flex items-start gap-2 text-sm leading-snug ${toneClasses.text}`}>
          <span className={`mt-1.5 h-1 w-1 rounded-full flex-shrink-0 ${toneClasses.dot}`} />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  </div>
);

/**
 * The Routy AI Summary briefing rendered as a chat card — the same content
 * the appointment details view generates (key points, before-arrival, sales
 * notes, follow-up, and a collapsible Sales Prep section when the meeting is
 * a sales opportunity).
 */
const AppointmentBriefCard: React.FC<{ artifact: RoutyAppointmentBriefArtifact }> = ({ artifact }) => {
  const [prepOpen, setPrepOpen] = useState(false);
  const insight = artifact.insight?.isSalesOpportunity ? artifact.insight : undefined;
  const angleCount = insight?.salesAngle?.length ?? 0;
  const questionCount = insight?.leadingQuestions?.reduce((sum, g) => sum + g.questions.length, 0) ?? 0;

  return (
    <div className="ml-10 rounded-lg bg-surface border border-accent-200 shadow-sm overflow-hidden max-w-md">
      <div className="px-4 py-2.5 bg-accent-50 border-b border-accent-200 flex items-center gap-2 min-w-0">
        <Sparkles className="w-4 h-4 text-accent-600 flex-shrink-0" />
        <div className="min-w-0">
          <p className="text-xs font-semibold text-accent-700 uppercase tracking-wider truncate">Appointment Brief</p>
          {(artifact.title || artifact.whenLabel) && (
            <p className="text-2xs text-ink-500 truncate">
              {[artifact.title, artifact.whenLabel].filter(Boolean).join(' · ')}
            </p>
          )}
        </div>
      </div>

      <div className="p-3 space-y-2.5">
        <p className="text-sm font-semibold text-ink-900 leading-snug">{artifact.headline}</p>

        {artifact.keyPoints.length > 0 && (
          <ul className="space-y-1.5">
            {artifact.keyPoints.map((point, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-ink-700 leading-snug">
                <span className="mt-1.5 h-1 w-1 rounded-full bg-accent-400 flex-shrink-0" />
                <span>{point}</span>
              </li>
            ))}
          </ul>
        )}

        {artifact.beforeArrival && artifact.beforeArrival.length > 0 && (
          <BulletSection
            icon={AlertTriangle}
            label="Before Arrival"
            items={artifact.beforeArrival}
            toneClasses={{
              wrap: 'bg-amber-50/80 border border-amber-200',
              label: 'text-amber-700',
              dot: 'bg-amber-500',
              text: 'text-ink-800',
            }}
          />
        )}

        {artifact.salesNotes && artifact.salesNotes.length > 0 && (
          <BulletSection
            icon={ClipboardList}
            label="Sales Notes"
            items={artifact.salesNotes}
            toneClasses={{ wrap: '', label: 'text-ink-400', dot: 'bg-ink-300', text: 'text-ink-700' }}
          />
        )}

        {insight && (
          <div className="pt-2 border-t border-accent-200/70">
            <button
              type="button"
              onClick={() => setPrepOpen((v) => !v)}
              aria-expanded={prepOpen}
              className="w-full flex items-center justify-between gap-2 group"
            >
              <span className="inline-flex items-center gap-1.5 text-2xs font-semibold text-warning-600 uppercase tracking-wider">
                <Target className="h-3.5 w-3.5" />
                Sales Prep
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-ink-400 group-hover:text-ink-600 transition-colors">
                {!prepOpen && (angleCount > 0 || questionCount > 0) && (
                  <span>
                    {[
                      angleCount > 0 ? `${angleCount} angle${angleCount === 1 ? '' : 's'}` : null,
                      questionCount > 0 ? `${questionCount} question${questionCount === 1 ? '' : 's'}` : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                )}
                {prepOpen ? 'Hide' : 'Show'}
                <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${prepOpen ? 'rotate-180' : ''}`} />
              </span>
            </button>

            {prepOpen && (
              <div className="mt-2.5 space-y-2.5">
                {(insight.meetingTypeLabel || insight.meetingTypeDescription) && (
                  <div className="rounded-lg bg-warning-50/60 border border-warning-200/70 px-3 py-2.5">
                    <p className="text-2xs font-semibold text-warning-600 uppercase tracking-wider mb-1">Meeting Type</p>
                    {insight.meetingTypeLabel && (
                      <p className="text-sm font-semibold text-ink-900 leading-snug">{insight.meetingTypeLabel}</p>
                    )}
                    {insight.meetingTypeDescription && (
                      <p className="mt-1 text-sm text-ink-600 leading-snug">{insight.meetingTypeDescription}</p>
                    )}
                  </div>
                )}

                {insight.salesAngle && insight.salesAngle.length > 0 && (
                  <ol className="space-y-2">
                    {insight.salesAngle.map((point, i) => (
                      <li key={i} className="flex items-start gap-2.5">
                        <span className="flex-shrink-0 mt-0.5 w-5 h-5 rounded-full bg-warning-100 text-warning-700 text-2xs font-bold flex items-center justify-center">
                          {i + 1}
                        </span>
                        <span className="text-sm text-ink-700 leading-snug">
                          <span className="font-semibold text-ink-900">{point.title}.</span> {point.detail}
                        </span>
                      </li>
                    ))}
                  </ol>
                )}

                {insight.leadingQuestions && insight.leadingQuestions.length > 0 && (
                  <div className="space-y-2">
                    {insight.leadingQuestions.map((group, gi) => (
                      <div key={gi} className="rounded-lg bg-ink-50/60 border border-ink-100 px-3 py-2.5">
                        <p className="text-xs font-semibold text-ink-600 mb-1.5">{group.category}</p>
                        <ul className="space-y-1.5 pl-4">
                          {group.questions.map((q, qi) => (
                            <li key={qi} className="text-sm text-ink-700 leading-snug list-disc marker:text-ink-300">
                              {q}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AppointmentBriefCard;
